import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Form, Button } from 'semantic-ui-react'
import FWOIteractiveTemplateRow from '../helpers/FWOIteractiveTemplateRow'
import {
  FormKey,
  IInteractiveTemplateField,
  IInteractiveTemplatePage,
  IInteractiveTemplateSection,
} from '../../../models/InteractiveTemplate'
import Conditional from '../../common/Conditional'
import {
  getInitialValues,
  getFieldSchema,
  buildAddressFromValues,
  getPageFields,
} from '../common'
import { FormRegex } from '../../../models/Common'
import Accordion from '../../common/Accordion'
import PageTitle from '../../PageTitle'

export const WarningLetterTemplate = (props) => {
  const [initialValues, setInitialValues] = useState({})
  const [validationSchema, setValidationSchema] = useState(null)
  const [showPerformanceMeetingPage, setShowPerformanceMeetingPage] =
    useState(false)
  const [showLetterDetailsPage, setShowLetterDetailsPage] = useState(false)
  const [attendMeeting, setAttendMeeting] = useState('')
  const [attendMeetElse, setAttendMeetElse] = useState('')
  const [previousWarning, setPreviousWarning] = useState('')
  const [validPerformanceMeeting, setValidPerformanceMeeting] = useState(false)
  const [performanceMeeting, setPerformanceMeeting] = useState('')

  const [currentPage, setCurrentPage] = useState(null)

  enum Page {
    PerformanceMeeting = 'Performance meeting',
    LetterDetails = 'Letter details',
  }

  useEffect(() => {
    const values = getInitialValues(props.template)
    setInitialValues(values)
  }, [])

  useEffect(() => {
    setCurrentPage(props.show ? Page.PerformanceMeeting : null)
  }, [props.show])

  useEffect(() => {
    const schema = getPageSchema(currentPage)
    setValidationSchema(schema)
    if (currentPage === Page.PerformanceMeeting) {
      setShowPerformanceMeetingPage(true)
      setShowLetterDetailsPage(false)
    } else if (currentPage === Page.LetterDetails) {
      setShowPerformanceMeetingPage(false)
      setShowLetterDetailsPage(true)
    }
  }, [currentPage])

  const getPageSchema = (currentPage) => {
    const page = props.template?.pages?.find(
      (page) => page.name === currentPage
    )

    if (!page) {
      return
    }

    const fields = getPageFields(page)

    const schema = fields.reduce((schema, field) => {
      if (
        field.replacement === 'BEHALF_NAME' ||
        field.replacement === 'BEHALF_POSITION'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('ATTEND_MEETING', {
            is: (ATTEND_MEETING) => ATTEND_MEETING === 'No',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }
      if (field.replacement === 'OTHER_ATTENDEE') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('ATTEND_MEET_ELSE', {
            is: (ATTEND_MEET_ELSE) => ATTEND_MEET_ELSE === 'Yes',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }
      if (
        field.replacement === 'GIVEN_WARNINGS' ||
        field.replacement === 'HOW_WARNING'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('PREVIOUS_WARNING', {
            is: (PREVIOUS_WARNING) => PREVIOUS_WARNING === 'Yes',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }
      if (field.replacement === 'RECENT_WARNING') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('PREVIOUS_WARNING', {
            is: (PREVIOUS_WARNING) => PREVIOUS_WARNING === 'Yes',
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .matches(
                FormRegex.Date,
                'Please enter a valid date in DD/MM/YYYY format'
              ),
          }),
        }
      }
      return getFieldSchema(schema, field)
    }, {})

    return Yup.object().shape(schema)
  }

  const renderField = (field: IInteractiveTemplateField, index: number) => {
    if (field.replacement === 'HOW_WARNING') {
      field.values = ['Verbally', 'In writing']
    }
    if (field.replacement === 'PERFORMANCE_MEET') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setPerformanceMeeting(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }
    if (
      field.replacement === 'PERFORMANCE_MEET_TIP' &&
      performanceMeeting !== 'no'
    ) {
      return null
    }
    if (field.replacement === 'ATTEND_MEETING') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setAttendMeeting(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (
      (field.replacement === 'BEHALF_SIGNATORY' ||
        field.replacement === 'BEHALF_NAME' ||
        field.replacement === 'BEHALF_POSITION') &&
      attendMeeting !== 'no'
    ) {
      return null
    }
    if (field.replacement === 'ATTEND_MEET_ELSE') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setAttendMeetElse(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }
    if (field.replacement === 'OTHER_ATTENDEE' && attendMeetElse !== 'yes') {
      return null
    }
    if (field.replacement === 'PREVIOUS_WARNING') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setPreviousWarning(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }
    if (
      (field.replacement === 'GIVEN_WARNINGS' ||
        field.replacement === 'RECENT_WARNING' ||
        field.replacement === 'HOW_WARNING') &&
      previousWarning !== 'yes'
    ) {
      return null
    }
    return (
      <FWOIteractiveTemplateRow
        key={index}
        {...field}
      ></FWOIteractiveTemplateRow>
    )
  }

  const validatePage = async (validateForm, setState) => {
    return validateForm().then((errors) => {
      console.log(errors)
      const state = Object.keys(errors).length === 0
      setState(state)
      return state
    })
  }

  const renderPage = (
    page: IInteractiveTemplatePage,
    index: number,
    validateForm
  ) => {
    if (page?.name === Page.PerformanceMeeting) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showPerformanceMeetingPage}
          onClick={() => {
            if (currentPage !== Page.PerformanceMeeting) {
              setCurrentPage(Page.PerformanceMeeting)
            }
            setShowPerformanceMeetingPage(!showPerformanceMeetingPage)
          }}
        >
          <Conditional if={showPerformanceMeetingPage}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button"
                onClick={() =>
                  validatePage(validateForm, setValidPerformanceMeeting)
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
    if (page?.name === Page.LetterDetails) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showLetterDetailsPage}
          onClick={() => {
            if (!currentPage) {
              return
            }
            validatePage(validateForm, setValidPerformanceMeeting).then(
              (isPrevPageValid: boolean) => {
                if (showLetterDetailsPage) {
                  // fold page
                  setShowLetterDetailsPage(false)
                } else {
                  if (isPrevPageValid) {
                    setCurrentPage(Page.LetterDetails)
                    setShowLetterDetailsPage(true)
                    window.scrollTo(0, 0)
                  } else {
                    // keep folded
                    setShowLetterDetailsPage(false)
                  }
                }
              }
            )
          }}
        >
          <Conditional if={validPerformanceMeeting}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button"
                onClick={() =>
                  validateForm().then((errors) => console.log(errors))
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
  }

  const renderSection = (
    section: IInteractiveTemplateSection,
    index: number
  ) => {
    if (section?.fields) {
      return (
        <section key={index}>
          <h3>{section.name}</h3>
          {section.fields.map((field, index) => renderField(field, index))}
        </section>
      )
    } else {
      return <></>
    }
  }

  const onSubmit = (values) => {
    if (performanceMeeting !== 'yes') {
      alert('You cannot continue')
      return
    }
    values['EMPLOYEE_ADDRESS'] = buildAddressFromValues(
      'EMPLOYEE_ADDRESS',
      values
    )

    setInitialValues(values)
    if (currentPage === Page.PerformanceMeeting) {
      setValidPerformanceMeeting(true)
      setCurrentPage(Page.LetterDetails)
    } else if (currentPage === Page.LetterDetails) {
      props.handleNext(values, FormKey.WarningLetter)
      setShowLetterDetailsPage(false)
    }
  }

  return (
    <>
      <PageTitle title="Warning letter - Templates - Fair Work Ombudsman" />
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}
        validateOnMount={false}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, validateForm }) => (
          <Form
            className="ui form osp-variant large"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="accordian">
              {props.template?.pages?.map((page, index) =>
                renderPage(page, index, validateForm)
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
